import React, { useState, useEffect } from "react";
import { Upload, Button, Typography, message, Progress } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { FileDetails, FileImage, FilePreview, UploadContainer } from "./styles";

interface FileUploadComponentProps {
  title?: string;
  style?: React.CSSProperties;
  isRequired?: boolean;
  value?: string | number | undefined;
  onChange?: (fileData: FileData | null) => void;
  placeholder?: string;
  error?: boolean;
  isDisabled?: boolean;
  size?: "small" | "middle" | "large";
  readonly?: boolean;
  dimensions: string;
  fileSize: string;
  fileSizeText: string;
  format: string;
  defaultValue?: string | null;
}

export interface FileData {
  name: string;
  size: number;
  type: string;
  url: string | ArrayBuffer | null;
  file: File | null;
}

const FileUploadComponent = ({
  title,
  style,
  isRequired = false,
  onChange,
  placeholder = "Dosya seçin",
  error = false,
  isDisabled,
  size = "large",
  readonly = false,
  dimensions,
  fileSize,
  fileSizeText,
  format,
  defaultValue
}: FileUploadComponentProps) => {
  const [fileInfo, setFileInfo] = useState<FileData | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const maxFileSize = Number(fileSize); // bytes
  const allowedFormats = ['image/jpeg', 'image/png'];

  const dimensionList = dimensions.split(',').map(dim => {
    const [width, height] = dim.trim().split('x').map(Number);
    return { width, height };
  });

  useEffect(() => {
    if (defaultValue) {
      setFileInfo({
        name: defaultValue?.split('/').pop() || 'default-image',
        size: 0,
        type: 'image/jpeg',
        url: defaultValue,
        file: null
      });
    }
  }, [defaultValue]);

  const beforeUpload = (file: File) => {
    if (file.size > maxFileSize) {
      message.error(`${file.name} dosyası maksimum boyutu aşıyor (${fileSizeText})`);
      return false;
    }

    if (!allowedFormats.includes(file.type)) {
      message.error(`${file.name} dosya formatı geçersiz. Sadece JPEG, PNG, JPG formatları desteklenmektedir`);
      return false;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadstart = () => {
      setUploadProgress(0);
    };
    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = (event.loaded / event.total) * 100;
        setUploadProgress(percentComplete);
      }
    };
    reader.onload = () => {
      const img = new Image();
      img.src = reader.result as string;

      img.onload = () => {
        const { width, height } = img;
        const isValidDimension = dimensionList.some(dim => dim.width === width && dim.height === height);

        if (!isValidDimension) {
          message.error(`${file.name} dosyasının boyutları ${dimensionList.map(d => `${d.width}x${d.height}`).join(', ')} olmalıdır`);
          return false;
        } else {
          const fileData: FileData = {
            name: file.name,
            size: file.size,
            type: file.type,
            url: reader.result,
            file: file
          };
          message.success(`${file.name} dosyası başarıyla yüklendi`);
          setFileInfo(fileData);
          if (onChange) onChange(fileData);
          setUploadProgress(100);
        }
      };
    };
    return false;
  };

  const handleRemove = () => {
    setFileInfo(null);
    message.success('Dosya başarıyla silindi');
    if (onChange) onChange(null);
    setUploadProgress(0);
  };

  return (
    <UploadContainer style={style}>
      {title && (
        <Typography.Title level={5} style={{ color: "#2e333e" }}>
          {title}
          {isRequired && <span style={{ color: "red" }}>&nbsp;*</span>}
        </Typography.Title>
      )}
      <Typography.Paragraph>
        Maksimum dosya boyutu: {fileSizeText}.<br /> Desteklenen formatlar: {format}.<br /> Görsel ölçüleri: {dimensions}
      </Typography.Paragraph>
      <Upload
        beforeUpload={beforeUpload}
        disabled={isDisabled || readonly}
        showUploadList={false}
        style={{ cursor: readonly ? "not-allowed" : "pointer" }}
      >
        <Button
          icon={<UploadOutlined />}
          size={size}
          disabled={isDisabled || readonly}
        >
          {placeholder}
        </Button>
      </Upload>
      {uploadProgress > 0 && uploadProgress < 100 && (
        <Progress percent={uploadProgress} />
      )}
      {fileInfo && (
        <>
          <FileDetails>
            {fileInfo.type.startsWith("image/") && (
              <FileImage
                src={fileInfo.url as string}
                alt={fileInfo.name}
              />
            )}
            {fileInfo.type.startsWith("text/") && (
              <FilePreview
                src={fileInfo.url as string}
                title="Text Preview"
              />
            )}
            <Button
              icon={<DeleteOutlined />}
              type="primary"
              danger
              onClick={handleRemove}
              style={{ marginTop: "auto" }}
            >
              Dosyayı Sil
            </Button>
          </FileDetails>

        </>
      )}
    </UploadContainer>
  );
};

export default FileUploadComponent;
